import * as DialogPrimitive from "@radix-ui/react-dialog";
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { cn } from "../../lib/utils";
import { useSurfaceThemeClasses } from "./surface-theme";
import { lockDocumentScroll } from "./document-scroll-lock";

const DialogContainer = createContext<HTMLElement | null>(null);
export function useDialogContainer() { return useContext(DialogContainer); }

export function Dialog({ title, pending, onClose, children, className, variant = "modal", open = true }: Readonly<{ title: string; pending: boolean; onClose: () => void; children: ReactNode; className?: string; variant?: "modal" | "drawer"; open?: boolean }>) {
  const theme = useSurfaceThemeClasses();
  const [element, setElement] = useState<HTMLElement | null>(null);
  const [overlay, setOverlay] = useState<HTMLElement | null>(null);
  // 必须在渲染期捕获打开前的活动元素：Radix FocusScope 在子组件 effect 中移入焦点，
  // 而 effect 捕获时焦点已在弹窗内，关闭时恢复会落空到 body。
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  const wasOpenRef = useRef(false);
  if (typeof document !== "undefined") {
    if (open && !wasOpenRef.current) restoreFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    wasOpenRef.current = open;
  }

  useEffect(() => {
    if (!element || !open) return;
    const unlockScroll = lockDocumentScroll();
    return () => { unlockScroll(); restoreFocusRef.current?.focus(); };
  }, [element, open]);
  // SSR 静态标记保留关闭态内容并对齐原生 dialog 的 DOM 语义（无 hydration，两者不冲突）；
  // 客户端按 Radix 标准在关闭时卸载。
  const forceMount = typeof document === "undefined" ? true : undefined;
  const content = <>
    <DialogPrimitive.Overlay ref={setOverlay} forceMount={forceMount} hidden={!open} className={cn("fixed inset-0 z-50", variant === "drawer" ? "bg-[#18181b55]" : "bg-zinc-950/50")} />
    <DialogPrimitive.Content
      ref={setElement}
      forceMount={forceMount}
      hidden={!open}
      aria-label={title}
      aria-busy={pending}
      onEscapeKeyDown={(event) => {
        // Nested controls that handle Escape themselves retain ownership of this key.
        if (pending || event.target instanceof Element && event.target.closest("[data-dialog-escape-boundary]")) event.preventDefault();
      }}
      // 背景（遮罩）点击关闭；弹层外其他节点（如全局 toast）不触发关闭，与原生 dialog 时代语义一致。
      onPointerDownOutside={(event) => { if (pending || event.target !== overlay) event.preventDefault(); }}
      // Radix 无 Trigger 时会把焦点恢复交给 triggerRef；焦点恢复由上方 effect 按打开前的活动元素执行。
      onCloseAutoFocus={(event) => event.preventDefault()}
      className={cn(variant === "drawer"
        ? "channel-editor"
        : "secret-modal fixed inset-0 z-50 m-auto h-fit !max-h-[calc(100dvh_-_36px)] !w-[calc(100%_-_36px)] !max-w-md !overflow-y-auto outline-none", theme, theme && "!bg-popover !text-popover-foreground backdrop-blur-xl", className)}
    >
      <DialogContainer.Provider value={element}><fieldset disabled={pending} className={cn("m-0 min-w-0 border-0 p-0", variant === "drawer" && "h-full min-h-0")}>{children}</fieldset></DialogContainer.Provider>
    </DialogPrimitive.Content>
  </>;
  // Radix 的 Portal 在 SSR 首帧不输出内容；测试环境的静态标记需要内联渲染。
  return <DialogPrimitive.Root open={open} onOpenChange={(next) => { if (!next && !pending) onClose(); }}>
    {typeof document === "undefined" ? content : createPortal(content, document.body)}
  </DialogPrimitive.Root>;

}
